/* eslint-disable max-len */
/**
 * @description Custom hooks for getting pilot easily.
 * If DataServiceContext cannot be gotten, use "getPilotValue" instead which needs to pass in
 * serverContext explicitly.
 *
 * Note: Define pilot name in "PILOTS"!
 */

import { useMemo, useContext } from 'react';
import { getPilotValue, PILOTS } from '@bingads-webui-campaign-react/smart-component-util';

import { DataServiceContext } from '../server-context';

export function usePilotsValue(key) {
  const serverContext = useContext(DataServiceContext);
  const isPilotEnabled = useMemo(
    () => (key ? getPilotValue(serverContext, key) : false),
    [key, serverContext]
  );

  return isPilotEnabled;
}

export const useIsSubscriptionAdsEnabled = () => {
  const signup = usePilotsValue(PILOTS.isSubscriptionSignupEnabled);
  const postSignup = usePilotsValue(PILOTS.isUnifiedProductSubscriptionEnabled);

  return signup || postSignup;
};

export const useIsBusinessHourSectionEnabled = () => usePilotsValue(PILOTS.isSmartPageBusinessHourSectionEnabled);
export const useIsSmartPageImageTextSuggestionEnabled = () => usePilotsValue(PILOTS.isSmartPageImageTextSuggestionEnabled);
export const useIsSmartPageM365StockImageEnabled = () => usePilotsValue(PILOTS.isSmartPageM365StockImageEnabled);
export const useIsSmartPageM365StockImageGalleryEnabled = () => usePilotsValue(PILOTS.isSmartPageM365StockImageGalleryEnabled);
export const useIsSmartPageVideoSectionEnabled = () => usePilotsValue(PILOTS.isSmartPageVideoSectionEnabled);
export const useIsSmartPageMapEnabled = () => usePilotsValue(PILOTS.isSmartPageMapEnabled);
export const useIsSmartPageCustomizedUpdateEnabled = () => usePilotsValue(PILOTS.isSmartPageCustomizedUpdateEnabled);
export const useIsSmartPageM365BannerEnabled = () => usePilotsValue(PILOTS.isSmartPageM365BannerEnabled);
export const useIsSmartPageDarkModeEnabled = () => usePilotsValue(PILOTS.isSmartPageDarkModeEnabled);
export const useIsSmartPageProfessionalServicesEnabled = () => usePilotsValue(PILOTS.isSmartPageProfessionalServicesEnabled);
export const useIsSmartPageBookAppointmentEnabled = () => usePilotsValue(PILOTS.isSmartPageBookAppointmentEnabled);
export const useIsSmartPageTemplateSuggestionEnabled = () => usePilotsValue(PILOTS.isSmartPageTemplateSuggestionEnabled);

// UI pilot, this pilot will never turn on in any environment, only for appending it in url for testing
const useIsSmartPageM365PremiumFeatureEnabled = () => usePilotsValue(PILOTS.isSmartPageM365PremiumFeatureEnabled);
// Account level pilot, not UI feature pilot, this value comes from an api call
const useIsM365PremiumUser = () => usePilotsValue(PILOTS.isM365PremiumUser);

// Only use this hook to control your UI feature, so it could control by the real MT flag
// or by manually appending queryString to enable the pilot in UI
export const useIsM365Premium = () => {
  const isM365PremiumUser = useIsM365PremiumUser();
  const isSmartPageM365PremiumFeatureEnabled = useIsSmartPageM365PremiumFeatureEnabled();

  return isM365PremiumUser || isSmartPageM365PremiumFeatureEnabled;
};
